import React, { useState, useEffect } from 'react';
import { useSystemProfile } from '../utils/systemProfile.js';
import { incrementDocCount } from '../utils/docCounter.js';
import { saveDocToHistory } from '../utils/docHistory.js';
import { callLLMWithMeta } from './llmService';
import { supabase } from '../lib/supabaseClient.js';
import { userKey } from '../utils/storageHelper.js';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// ── Document type config ──
const DOC_TYPES = [
  { id: 'policy',       label: '📋 AI Policy',                color: '#3b82f6' },
  { id: 'assessment',   label: '📊 Impact Assessment',        color: '#8b5cf6' },
  { id: 'dataplan',     label: '📁 Data Management Plan',     color: '#06b6d4' },
  { id: 'transparency', label: '📢 Transparency Disclosure',  color: '#f59e0b' },
];

// ── Framework → audit cycle (days) ──
const cycleDays = (framework) => {
  if (framework === 'nist') return 90;
  if (framework === 'eu')   return 365;
  if (framework === 'iso')  return 365;
  return 365; // default
};

const cycleLabel = (framework) => {
  if (framework === 'nist') return 'NIST AI RMF (90-day cycle)';
  if (framework === 'eu')   return 'EU AI Act (365-day cycle)';
  if (framework === 'iso')  return 'ISO/IEC 42001 (365-day cycle)';
  return '365-day cycle (default)';
};

// ── Per-doc-type token budgets ──
// The 'transparency' doc has 9 sections with implementation guidance for each —
// it's the longest of the four and was getting truncated at the old flat 2000 limit.
const MAX_TOKENS_BY_TYPE = {
  policy: 3000,
  assessment: 3000,
  dataplan: 3000,
  transparency: 4500,
};

// Dev-only gate for the audit-cycle reset button below.
// This app is built with Create React App (webpack), not Vite, so we use
// process.env.NODE_ENV — CRA's build tooling replaces this at build time via
// webpack's DefinePlugin ('development' in `npm start` / `vercel dev`,
// 'production' in `npm run build`), so the check — and the button — is
// compiled away entirely in a production build.
const isDev = process.env.NODE_ENV !== 'production';

function DocumentGenerator({ onNavigate }) {
  const _profile = useSystemProfile();
  const [companyName, setCompanyName] = useState(() => _profile.companyName || '');
  const [industry, setIndustry] = useState(() => _profile.industry || '');
  const [aiSystem, setAiSystem] = useState(() => _profile.systemPurpose || '');
  const [docType, setDocType] = useState('policy');
  const [generatedDoc, setGeneratedDoc] = useState('');
  const [loading, setLoading] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Per-type stored docs: { policy: {content, generated_at, expires_at, framework}, ... }
  // active = expires_at > now (locks generate button + shows the doc inline)
  // archived = expires_at <= now (shows old doc + allows regenerate)
  const [storedDocs, setStoredDocs] = useState({ policy: null, assessment: null, dataplan: null, transparency: null });
  const [loadingStored, setLoadingStored] = useState(true);

  // Risk result needed for primaryFramework + Q8 gate
  const [riskResult, setRiskResult] = useState(null);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(userKey('qais_risk_result'));
      setRiskResult(raw ? JSON.parse(raw) : null);
    } catch {}
  }, []);

  // ── Load existing docs on mount: prefer Supabase, fall back to localStorage ──
  useEffect(() => {
    (async () => {
      setLoadingStored(true);
      const merged = { policy: null, assessment: null, dataplan: null, transparency: null };

      // Local-first
      try {
        for (const t of ['policy', 'assessment', 'dataplan', 'transparency']) {
          const raw = localStorage.getItem(userKey(`qais_doc_${t}`));
          if (raw) merged[t] = JSON.parse(raw);
        }
      } catch {}

      // Supabase override (most recent per type)
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data, error } = await supabase
            .from('user_documents')
            .select('doc_type, content, framework, generated_at, expires_at')
            .eq('user_id', user.id)
            .order('generated_at', { ascending: false });
          if (!error && data) {
            // Take latest row per doc_type
            const byType = {};
            data.forEach(d => { if (!byType[d.doc_type]) byType[d.doc_type] = d; });
            for (const t of ['policy', 'assessment', 'dataplan', 'transparency']) {
              if (byType[t]) {
                merged[t] = {
                  content:      byType[t].content,
                  framework:    byType[t].framework,
                  generated_at: byType[t].generated_at,
                  expires_at:   byType[t].expires_at,
                };
                localStorage.setItem(userKey(`qais_doc_${t}`), JSON.stringify(merged[t]));
              }
            }
          }
        }
      } catch (e) {

      }

      setStoredDocs(merged);
      setLoadingStored(false);
    })();
  }, []);

  // ── Helpers ──
  const isActive = (rec) => {
    if (!rec || !rec.expires_at) return false;
    return new Date(rec.expires_at).getTime() > Date.now();
  };

  const currentRecord = storedDocs[docType];
  const currentIsActive   = isActive(currentRecord);
  const currentIsArchived = currentRecord && !currentIsActive;
  const hasRiskScore = !!(riskResult && riskResult.primaryFramework);
  const userFramework = riskResult?.primaryFramework || null;

  // When user switches doc type, show the saved (active OR archived) content if any
  useEffect(() => {
    if (currentRecord && currentRecord.content) {
      setGeneratedDoc(currentRecord.content);
    } else {
      setGeneratedDoc('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docType, storedDocs]);

  // ── Detect a response that was cut off mid-generation (hit max_tokens) ──
  // Heuristic: a properly finished document should end in sentence-ending
  // punctuation, a closing markdown element, or a bullet/list line — not
  // mid-word, mid-table-row, or on a dangling label like "R-".
  const looksTruncated = (text) => {
    if (!text) return true;
    const trimmed = text.trim();
    const tail = trimmed.slice(-40);
    // Ends with an open table row / dangling short token (e.g. "R-", "| R-001 |")
    if (/[|\-–—:]\s*$/.test(trimmed)) return true;
    // Ends mid-sentence with no terminal punctuation and no closing bullet
    if (!/[.!?:)"'”\]]\s*$/.test(trimmed) && !/^[-*•]/.test(tail.split('\n').pop() || '')) {
      return true;
    }
    return false;
  };

  // ── DEV ONLY: reset the audit cycle for the current doc type ──
  // Clears the Supabase row and the localStorage cache so isActive() goes
  // false immediately, letting you regenerate without waiting out the
  // 90/365-day cycle. Never rendered in production (gated by isDev below).
  const resetAuditCycle = async () => {
    if (!isDev) return; // belt-and-suspenders — button is already hidden in prod
    if (!window.confirm(`Reset the audit cycle for "${DOC_TYPES.find(t => t.id === docType)?.label || docType}"? This clears its expiry so you can regenerate immediately.`)) {
      return;
    }
    setResetting(true);
    try {
      try { localStorage.removeItem(userKey(`qais_doc_${docType}`)); } catch {}

      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await supabase
            .from('user_documents')
            .delete()
            .eq('user_id', user.id)
            .eq('doc_type', docType);
        }
      } catch (e) {
        console.warn('Supabase reset failed (dev):', e);
      }

      setStoredDocs(prev => ({ ...prev, [docType]: null }));
      setGeneratedDoc('');
    } finally {
      setResetting(false);
    }
  };

  // ── Generate ──
  const generateDocument = async () => {
    if (!hasRiskScore) {
      alert('Please complete the Risk Score assessment first. We use your primary framework to set the document audit cycle.');
      return;
    }
    if (currentIsActive) {
      alert('This document type is already generated for the current audit cycle. You can regenerate after the cycle expires.');
      return;
    }
    if (!companyName || !industry || !aiSystem) {
      alert('Please fill in all fields!');
      return;
    }

    setLoading(true);
    setGeneratedDoc('');

    let promptText = '';
    const fwLabel = cycleLabel(userFramework);

    if (docType === 'policy') {
      const profileCtx = _profile.promptContext || `Company: ${companyName}\nIndustry: ${industry}\nAI System: ${aiSystem}`;
      promptText = `Create a professional AI Policy document. Context:\n${profileCtx}\n\nTheir AI system: ${aiSystem}\nPrimary framework: ${fwLabel}\n\nInclude these sections:\n1. Purpose and Scope\n2. AI Principles (transparency, fairness, accountability, safety, privacy)\n3. Roles and Responsibilities\n4. Governance Framework\n5. Risk Management\n6. Review Process\n\nMake it professional and specific to their industry. Use clear headings and bullet points. Do not use Markdown table syntax (pipes | or --- separator rows) — present any tabular information as indented labeled lines or bullet points instead.`;
    } else if (docType === 'assessment') {
      promptText = `Create an AI Impact Assessment for ${companyName}'s AI system.\n\nCompany: ${companyName} (${industry} industry)\nAI System: ${aiSystem}\nPrimary framework: ${fwLabel}\n\nInclude these sections:\n1. System Description (what it does, how it works, who uses it)\n2. Intended Purpose and Use Cases\n3. Risk Assessment (potential harms to individuals, bias, privacy, safety)\n4. Stakeholder Impact Analysis (users, employees, society)\n5. Ethical Considerations\n6. Mitigation Strategies (risk reduction, monitoring, human oversight)\n\nMake it detailed and professional. Do not use Markdown table syntax (pipes | or --- separator rows) — present any tabular information (like a risk register) as indented labeled lines or bullet points instead, e.g. "R-001 — Bias in outputs: ...".`;
    } else if (docType === 'dataplan') {
      promptText = `Create a Data Management Plan for ${companyName}'s AI system.\n\nCompany: ${companyName} (${industry} industry)\nAI System: ${aiSystem}\nPrimary framework: ${fwLabel}\n\nInclude these sections:\n1. Data Sources (where data comes from, types collected)\n2. Data Quality Requirements (accuracy, completeness, freshness)\n3. Data Governance Framework (ownership, access controls, privacy)\n4. Data Lifecycle Management (collection, storage, retention, deletion)\n5. Compliance (GDPR/privacy law, ${industry}-specific regulations)\n6. Bias Prevention (representativeness, bias testing)\n\nMake it specific and actionable. Do not use Markdown table syntax (pipes | or --- separator rows) — present any tabular information as indented labeled lines or bullet points instead.`;
    } else if (docType === 'transparency') {
      // Article 50's enforcement date is a fixed legal fact (Aug 2, 2026), but whether
      // it's "upcoming" or "already in force" depends on today's date — hardcoding one
      // static phrase here would silently go stale the moment that date passed. Compute
      // the correct tense at generation time instead, so the prompt (and therefore the
      // AI's guidance) stays accurate whenever this is actually run.
      const ART_50_ENFORCEMENT = new Date('2026-08-02T00:00:00Z');
      const isArt50InForce = new Date() >= ART_50_ENFORCEMENT;
      const art50Status = isArt50InForce
        ? `already in force as of August 2, 2026 — organizations must already be compliant, and any gaps represent a current, ongoing compliance failure, not a future deadline`
        : `not yet in force — it takes effect August 2, 2026, so organizations have time to prepare but should not delay`;

      promptText = `Create an EU AI Act Article 50 Transparency Disclosure document for ${companyName}'s AI system.\n\nCompany: ${companyName} (${industry} industry)\nAI System: ${aiSystem}\nPrimary framework: ${fwLabel}\n\nThis document must satisfy EU AI Act Article 50 transparency obligations. As of today, this requirement is ${art50Status}. Frame all guidance in the document accordingly — do not describe it as an upcoming deadline if it is already in force. Include these sections:\n1. AI System Identification (name, version, provider, purpose)\n2. User Notification Statement — clear disclosure that users are interacting with an AI system (Art. 50(1))\n3. AI-Generated Content Marking — how outputs are labelled as AI-generated in a machine-readable format (Art. 50(2))\n4. Deepfake / Synthetic Content Disclosure — if applicable, how synthetic media is disclosed (Art. 50(4))\n5. Emotion Recognition / Biometric Categorisation Disclosure — if applicable (Art. 50(3))\n6. Transparency to Downstream Deployers — information provided to organisations using this AI system\n7. Public-Facing Transparency Summary — plain-language summary suitable for end users\n8. Designated Contact for Transparency Inquiries\n9. Review Schedule and Version Control\n\nMake it specific to their AI system and industry. Use clear headings. Include practical implementation guidance for each section — not just what is required but how to implement it. Do not use Markdown table syntax (pipes | or --- separator rows) — present any tabular information as indented labeled lines or bullet points instead.`;
    }

    const maxTokensForType = MAX_TOKENS_BY_TYPE[docType] || 3000;

    try {
      const first = await callLLMWithMeta({
        messages: [
          { role: 'system', content: 'You are an expert at creating AI compliance documents. Create professional, detailed documents that meet ISO 42001, EU AI Act, and NIST AI RMF standards. Format with clear sections and headers. Never use Markdown table syntax (| pipes or --- separator rows); use indented labeled lines or bullet points for any tabular information instead. Formatting must be consistent throughout the entire document: always use "- " (hyphen plus space) for bullet points — never "•" or "*" as a bullet marker. Always use "**Label:**" (double asterisk) to bold a label — never a single asterisk like "*Label:*". Do not mix styles within the same document.' },
          { role: 'user', content: promptText },
        ],
        temperature: 0.7,
        max_tokens: maxTokensForType,
      });
      let content = first.content;
      const firstProvider = first.provider; // e.g. "Groq" — which provider actually answered

      // ── If the response looks cut off, ask the model to finish it rather than
      // silently handing the user a broken document. One continuation attempt.
      // We ask the proxy to prefer the SAME provider that wrote the first half
      // (via preferredProvider), so the continuation is less likely to come from
      // a different model with different formatting habits. The proxy still
      // falls back to the rest of the chain if that provider has since failed
      // (e.g. hit a rate limit between the two calls). ──
      if (looksTruncated(content)) {
        try {
          const continuation = await callLLMWithMeta({
            messages: [
              { role: 'system', content: 'You are an expert at creating AI compliance documents. Never use Markdown table syntax (| pipes or --- separator rows); use indented labeled lines or bullet points instead. Formatting must stay consistent with the text already generated below: match its existing bullet marker and bold style exactly rather than switching conventions partway through.' },
              { role: 'user', content: promptText },
              { role: 'assistant', content },
              { role: 'user', content: 'Your previous response was cut off before it finished. Continue exactly where you left off — do not repeat any earlier content, and do not restart the document.' },
            ],
            temperature: 0.7,
            max_tokens: maxTokensForType,
            preferredProvider: firstProvider,
          });
          content = content + continuation.content;
        } catch (contErr) {
          console.warn('Continuation attempt failed, keeping original (possibly truncated) content:', contErr);
        }
      }

      // ── Persist: localStorage + Supabase ──
      const now = new Date();
      const expires = new Date(now.getTime() + cycleDays(userFramework) * 86400 * 1000);
      const record = {
        content,
        framework:    userFramework,
        generated_at: now.toISOString(),
        expires_at:   expires.toISOString(),
      };

      try { localStorage.setItem(userKey(`qais_doc_${docType}`), JSON.stringify(record)); } catch {}

      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await supabase.from('user_documents').insert({
            user_id:      user.id,
            doc_type:     docType,
            content,
            framework:    userFramework,
            generated_at: record.generated_at,
            expires_at:   record.expires_at,
          });
        }
      } catch (e) {

      }

      setStoredDocs(prev => ({ ...prev, [docType]: record }));
      setGeneratedDoc(content);
      incrementDocCount();
      saveDocToHistory({
        tool:      'document_generator',
        title:     `${DOC_TYPES.find(t => t.id === docType)?.label || docType}${companyName ? ' — ' + companyName : ''}`,
        preview:   content.slice(0, 120),
        framework: 'ISO 42001 / EU AI Act / NIST RMF',
      });
      window.dispatchEvent(new CustomEvent('qais-journey-update'));

    } catch (error) {
      console.error('All providers failed:', error);
      setGeneratedDoc('❌ All AI providers are currently unavailable. Please try again in a moment.');
    }

    setLoading(false);
  };

// ── Helpers for styled PDF (same approach as Risk Score / EU Classifier / Checklist) ──
  const loadImageAsBase64 = async (path) => {
    try {
      const response = await fetch(path);
      const blob = await response.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      console.warn('Logo load failed:', e);
      return null;
    }
  };

  // ── Convert a block of Markdown-table lines into an HTML <table> ──
  const renderMarkdownTable = (lines) => {
    // lines: array of raw '| a | b |' strings, including the '|---|---|' separator row
    const parseRow = (line) =>
      line
        .trim()
        .replace(/^\|/, '')
        .replace(/\|$/, '')
        .split('|')
        .map((cell) => cell.trim());

    const rows = lines
      .map(parseRow)
      // drop the '---' separator row
      .filter((cells) => !cells.every((c) => /^:?-{2,}:?$/.test(c)));

    if (rows.length === 0) return '';

    const [headerRow, ...bodyRows] = rows;
    const headerHtml = `<tr>${headerRow
      .map(
        (cell) =>
          `<th style="border: 1px solid #cbd5e1; background: #e2e8f0; color: #0e7c7c; padding: 6px 10px; text-align: left; font-size: 13px;">${cell}</th>`
      )
      .join('')}</tr>`;

    const bodyHtml = bodyRows
      .map(
        (cells) =>
          `<tr>${cells
            .map(
              (cell) =>
                `<td style="border: 1px solid #cbd5e1; padding: 6px 10px; font-size: 13px; vertical-align: top;">${cell}</td>`
            )
            .join('')}</tr>`
      )
      .join('');

    return `<table style="border-collapse: collapse; width: 100%; margin: 10px 0 16px 0;">${headerHtml}${bodyHtml}</table>`;
  };

        const renderAiMarkdown = (text) => {
          if (!text) return '';

          // ── Pull out Markdown table blocks first and replace with real <table> HTML,
          // so the raw '|' syntax never reaches the plain-text formatting below. ──
          const lines = text.split('\n');
          const outLines = [];
          let i = 0;
          while (i < lines.length) {
            const line = lines[i];
            const isTableRow = /^\s*\|.*\|\s*$/.test(line);
            const nextIsSeparator = i + 1 < lines.length && /^\s*\|?[\s:|-]*-{2,}[\s:|-]*\|?\s*$/.test(lines[i + 1]);
            if (isTableRow && nextIsSeparator) {
              const tableLines = [];
              while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) {
                tableLines.push(lines[i]);
                i++;
              }
              outLines.push(renderMarkdownTable(tableLines));
            } else {
              outLines.push(line);
              i++;
            }
          }
          text = outLines.join('\n');

          return text
            // Strip setext-style dividers (=== --- ___)
            .replace(/^[=\-_]{3,}\s*$/gm, '')
            // ### Heading 3 — dark cyan, underlined, bold
            .replace(/^#{3}\s+(.+)$/gm, '<div style="color: #0e7c7c; text-decoration: underline; font-weight: bold; font-size: 16px; margin: 14px 0 6px 0;">$1</div>')
            // ## Heading 2
            .replace(/^#{2}\s+(.+)$/gm, '<div style="color: #0e7c7c; text-decoration: underline; font-weight: bold; font-size: 18px; margin: 16px 0 8px 0;">$1</div>')
            // # Heading 1
            .replace(/^#{1}\s+(.+)$/gm, '<div style="color: #0e7c7c; text-decoration: underline; font-weight: bold; font-size: 20px; margin: 18px 0 10px 0;">$1</div>')
            // Bullet lines — the model inconsistently uses "-", "*", or a literal "•" as the
            // prefix. Normalize all three to the same "• " marker so bullets look uniform
            // regardless of which style this particular response happened to use.
            .replace(/^[•*-]\s+(.+)$/gm, '<div style="margin-left: 18px; margin-bottom: 4px;">• $1</div>')
            // Inline **bold**
            .replace(/\*\*(.+?)\*\*/g, '<span style="color: #0e7c7c; text-decoration: underline; font-weight: bold;">$1</span>')
            // Inline *bold* (single asterisk) — the model sometimes emphasizes labels this way
            // instead of "**...**". Render it identically to double-asterisk bold rather than
            // leaving the literal "*" characters on the page. Runs after the double-asterisk
            // pass above, so by this point no "**" pairs remain to conflict with.
            .replace(/\*([^*\n]+?)\*/g, '<span style="color: #0e7c7c; text-decoration: underline; font-weight: bold;">$1</span>')
            // Inline _italic_
            .replace(/(?<![\w_])_([^_\n]+)_(?![\w_])/g, '<em>$1</em>')
            // Cleanup
            .replace(/<\/div>\n+/g, '</div>')
            .replace(/<\/table>\n+/g, '</table>')
            .replace(/\n{2,}/g, '\n')
            .replace(/\n/g, '<br>')
            // A <table> that got wrapped in <br> tags on either side reads oddly — trim them
            .replace(/<br>\s*(<table)/g, '$1')
            .replace(/(<\/table>)\s*<br>/g, '$1');
        };

  const downloadPDF = async () => {
    if (!generatedDoc) return;
    setPdfGenerating(true);
    await new Promise(resolve => setTimeout(resolve, 50));

    const logoBase64 = await loadImageAsBase64('/logo.png');
    const docTypeConfig = DOC_TYPES.find(t => t.id === docType) || DOC_TYPES[0];
    const docTypeLabel = docTypeConfig.label.replace(/^[^\s]+\s/, ''); // strip leading emoji
    const docColor     = docTypeConfig.color;
    const coverTitle   = `${docTypeLabel}${companyName ? ` — ${companyName}` : ''}`;
    const headerTitle  = `Quantum AI Shield · ${docTypeLabel}`;
    const cycle        = cycleLabel(userFramework);
    const generatedAt  = currentRecord?.generated_at ? new Date(currentRecord.generated_at) : new Date();
    const nextDate     = currentRecord?.expires_at   ? new Date(currentRecord.expires_at)   : null;

    const htmlContent = `
      <div style="font-family: 'Inter', 'Helvetica', sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; background: white; color: #1e293b; line-height: 1.6; font-size: 15px;">
        ${logoBase64 ? `<div style="text-align: center; margin-bottom: 8px;"><img src="${logoBase64}" style="max-width: 180px; max-height: 90px;" alt="QuantumAI Shield" /></div>` : ''}
        <h1 style="color: #0e7c7c; border-bottom: 3px solid #0e7c7c; padding-bottom: 10px; font-size: 28px; text-align: center;">${coverTitle}</h1>
        <p style="text-align: center;"><strong>Generated by QuantumAI Shield</strong><br>${generatedAt.toLocaleDateString()}</p>

        <!-- Document info band -->
        <div style="background: ${docColor}1a; border: 3px solid ${docColor}; border-radius: 12px; padding: 18px; margin: 20px 0; text-align: center;">
          <div style="font-size: 12px; color: ${docColor}; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; margin-bottom: 6px;">Document Type</div>
          <div style="font-size: 22px; color: ${docColor}; font-weight: 800; margin-bottom: 6px;">${docTypeLabel.toUpperCase()}</div>
          <div style="font-size: 13px; color: #475569;">
            <strong>Framework:</strong> ${cycle}
            &nbsp;|&nbsp; <strong>Generated:</strong> ${generatedAt.toLocaleDateString()}
            ${nextDate ? `&nbsp;|&nbsp; <strong>Next regeneration:</strong> ${nextDate.toLocaleDateString()}` : ''}
          </div>
        </div>

        ${companyName || industry || aiSystem ? `
          <h2 style="color: #0e7c7c; border-bottom: 2px solid #0e7c7c; font-size: 22px;">Organization Details</h2>
          <p>
            ${companyName ? `<strong>Company:</strong> ${companyName}<br>` : ''}
            ${industry    ? `<strong>Industry:</strong> ${industry}<br>` : ''}
            ${aiSystem    ? `<strong>AI System:</strong> ${aiSystem}` : ''}
          </p>
        ` : ''}

        <h2 style="color: #0e7c7c; border-bottom: 2px solid #0e7c7c; font-size: 22px;">${docTypeLabel}</h2>
        <div style="background: #f8fafc; padding: 16px 20px; border-radius: 10px; font-size: 15px; line-height: 1.7; word-wrap: break-word; border-left: 4px solid ${docColor};">
          ${renderAiMarkdown(generatedDoc)}
        </div>

        <h2 style="color: #0e7c7c; border-bottom: 2px solid #0e7c7c; font-size: 22px;">Disclaimer</h2>
        <p style="font-size: 12px; color: #64748b;">AI‑generated compliance guidance – not legal advice. Always verify with a qualified professional. For questions, contact QuantumAIshield support. Blockchain records are permanent – please confirm before sealing.</p>
      </div>
    `;

    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '0';
    container.style.width = '800px';
    container.style.backgroundColor = 'white';
    container.style.zIndex = '-1';
    container.innerHTML = htmlContent;
    document.body.appendChild(container);

    try {
      await new Promise(resolve => setTimeout(resolve, 300));
      const canvas = await html2canvas(container, { scale: 2, backgroundColor: '#ffffff', logging: false, useCORS: false });

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth  = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const HEADER_HEIGHT = 28;
      const FOOTER_HEIGHT = 22;
      const SIDE_MARGIN   = 14; // mm — page-level left/right margin (was 0, edge-to-edge)

      const usableWidth  = pdfWidth - 2 * SIDE_MARGIN;
      const usableHeight = pdfHeight - HEADER_HEIGHT - FOOTER_HEIGHT;
      const imgWidth     = usableWidth;
      const imgHeight    = (canvas.height * imgWidth) / canvas.width;

      // ── Smart slicing: detect blank rows for clean page breaks ──
      const canvasCtx = canvas.getContext('2d');
      const imageData = canvasCtx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      const cw = canvas.width;

      const isRowBlank = (y) => {
        // A row is "safe to slice at" if it has very few dark pixels
        // (text pixels are dark; backgrounds — white OR colored — are light/medium)
        let darkPixels = 0;
        for (let x = 0; x < cw; x += 4) {
          const i = (y * cw + x) * 4;
          if (data[i] < 100 || data[i + 1] < 100 || data[i + 2] < 100) {
            darkPixels++;
            if (darkPixels > 2) return false; // 3+ dark pixels = has text
          }
        }
        return true;
      };

      const pxPerMm = canvas.height / imgHeight;
      const usableHeightPx = Math.floor(usableHeight * pxPerMm);

      const blankRows = [];
      for (let y = 0; y < canvas.height; y += 2) {
        if (isRowBlank(y)) blankRows.push(y);
      }

      const pageBreaks = [0];
      let currentY = 0;
      while (currentY < canvas.height) {
        const target = currentY + usableHeightPx;
        if (target >= canvas.height) break;
        let breakAt = target;
        // Search wider range (35% to 100% of page) for a blank row
        const minAllowed = currentY + usableHeightPx * 0.35;
        for (let i = blankRows.length - 1; i >= 0; i--) {
          if (blankRows[i] <= target && blankRows[i] > minAllowed) {
            breakAt = blankRows[i];
            break;
          }
        }
        pageBreaks.push(breakAt);
        currentY = breakAt;
      }

      // Drop trailing blank pages
      let lastContentRow = 0;
      for (let y = canvas.height - 1; y >= 0; y -= 2) {
        if (!isRowBlank(y)) { lastContentRow = y; break; }
      }
      while (pageBreaks.length > 1 && pageBreaks[pageBreaks.length - 1] >= lastContentRow - 20) {
        pageBreaks.pop();
      }
      const totalPages = pageBreaks.length;

      const drawHeaderFooter = (pageNum) => {
        if (pageNum > 1) {
          pdf.setFontSize(11);
          pdf.setTextColor(80, 80, 80);
          const logoW  = logoBase64 ? 10 : 0;
          const gap    = logoBase64 ? 3  : 0;
          const titleW = pdf.getTextWidth(headerTitle);
          const groupW = logoW + gap + titleW;
          const groupStartX = (pdfWidth - groupW) / 2;
          if (logoBase64) { try { pdf.addImage(logoBase64, 'PNG', groupStartX, 6, 10, 10); } catch {} }
          pdf.text(headerTitle, groupStartX + logoW + gap, 13);
          pdf.setDrawColor(200, 200, 200);
          pdf.setLineWidth(0.2);
          pdf.line(SIDE_MARGIN + 4, HEADER_HEIGHT - 4, pdfWidth - SIDE_MARGIN - 4, HEADER_HEIGHT - 4);
        }
        pdf.setFontSize(9);
        pdf.setTextColor(120, 120, 120);
        pdf.line(SIDE_MARGIN + 4, pdfHeight - FOOTER_HEIGHT + 6, pdfWidth - SIDE_MARGIN - 4, pdfHeight - FOOTER_HEIGHT + 6);
        pdf.text('www.QuantumAIShield.com',          SIDE_MARGIN + 4,            pdfHeight - 8);
        pdf.text(`Page ${pageNum} of ${totalPages}`, pdfWidth / 2,               pdfHeight - 8, { align: 'center' });
        pdf.text(new Date().toLocaleDateString(),    pdfWidth - SIDE_MARGIN - 4, pdfHeight - 8, { align: 'right' });
      };

      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        if (pageNum > 1) pdf.addPage();
        const startPx = pageBreaks[pageNum - 1];
        const endPx   = pageNum < totalPages ? pageBreaks[pageNum] : canvas.height;
        const slicePx = endPx - startPx;
        const sliceMm = slicePx / pxPerMm;

        const tempCanvas = document.createElement('canvas');
        tempCanvas.width  = canvas.width;
        tempCanvas.height = slicePx;
        const tempCtx = tempCanvas.getContext('2d');
        tempCtx.fillStyle = '#ffffff';
        tempCtx.fillRect(0, 0, canvas.width, slicePx);
        tempCtx.drawImage(canvas, 0, startPx, canvas.width, slicePx, 0, 0, canvas.width, slicePx);
        const sliceData = tempCanvas.toDataURL('image/png');

        pdf.addImage(sliceData, 'PNG', SIDE_MARGIN, HEADER_HEIGHT, imgWidth, sliceMm);
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pdfWidth, HEADER_HEIGHT, 'F');
        pdf.rect(0, pdfHeight - FOOTER_HEIGHT, pdfWidth, FOOTER_HEIGHT, 'F');
        drawHeaderFooter(pageNum);
      }

      pdf.save(`${companyName || 'AI'}_${docType}_${Date.now()}.pdf`);
    } catch (err) {
      console.error('PDF generation error:', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      document.body.removeChild(container);
      setPdfGenerating(false);
    }
  };

  // ── If no risk score yet (Q8a): block + show CTA ──
  if (!loadingStored && !hasRiskScore) {
    return (
      <div style={{ padding: '40px', maxWidth: '700px', margin: '0 auto', textAlign: 'center' }}>
        <div style={{ fontSize: '64px', marginBottom: '20px' }}>📊</div>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#0f172a', marginBottom: '12px' }}>
          Complete Risk Score first
        </h1>
        <p style={{ fontSize: '15px', color: '#64748b', marginBottom: '24px', lineHeight: '1.6' }}>
          Document generation uses your <strong>primary framework</strong> (NIST 90-day, ISO 365-day, or EU AI Act 365-day cycle) to set how often each document type can be regenerated.
          <br /><br />
          Please complete the Risk Score assessment so we know which audit cycle applies to you.
        </p>
        {onNavigate && (
          <button
            onClick={() => onNavigate('risk')}
            style={{ padding: '14px 28px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '10px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer' }}
          >
            ⚖️ Go to Risk Score
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: '40px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '10px', color: '#0f172a' }}>
        📄 Document Generator
      </h1>
      <p style={{ fontSize: '18px', color: '#64748b', marginBottom: '8px' }}>
        AI-powered compliance document creation.
      </p>
      <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '30px' }}>
        🔒 Each document type can be generated <strong>once per audit cycle</strong> ({cycleLabel(userFramework)}). After the cycle expires, you can regenerate; the previous version is kept for download.
      </p>

      {/* Input Form */}
      <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '15px', boxShadow: '0 4px 6px rgba(0,0,0,0.07)', marginBottom: '30px' }}>
        <h2 style={{ fontSize: '24px', marginBottom: '20px', color: '#0f172a' }}>Tell us about your company</h2>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#334155' }}>Company Name *</label>
          <input type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="e.g., Acme AI Solutions"
            style={{ width: '100%', padding: '12px', fontSize: '18px', border: '2px solid #e2e8f0', borderRadius: '8px', outline: 'none' }} />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#334155' }}>Industry *</label>
          <select value={industry} onChange={(e) => setIndustry(e.target.value)}
            style={{ width: '100%', padding: '12px', fontSize: '18px', border: '2px solid #e2e8f0', borderRadius: '8px', outline: 'none', backgroundColor: 'white' }}>
            <option value="">Select industry...</option>
            <option value="Healthcare">Healthcare</option>
            <option value="Finance">Finance</option>
            <option value="Retail">Retail</option>
            <option value="Education">Education</option>
            <option value="Manufacturing">Manufacturing</option>
            <option value="Technology">Technology</option>
            <option value="Legal">Legal</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#334155' }}>AI System Description *</label>
          <textarea value={aiSystem} onChange={(e) => setAiSystem(e.target.value)} placeholder="e.g., Customer service chatbot..."
            rows="4"
            style={{ width: '100%', padding: '12px', fontSize: '18px', border: '2px solid #e2e8f0', borderRadius: '8px', outline: 'none', resize: 'vertical', fontFamily: 'inherit' }} />
        </div>

        {/* Document Type Buttons — show lock state */}
        <div style={{ marginBottom: '25px' }}>
          <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600', color: '#334155' }}>Document Type *</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '15px' }}>
            {DOC_TYPES.map(({ id, label, color }) => {
              const rec = storedDocs[id];
              const active = isActive(rec);
              const archived = rec && !active;
              const selected = docType === id;
              return (
                <button
                  key={id}
                  onClick={() => setDocType(id)}
                  style={{
                    padding: '15px',
                    backgroundColor: selected ? color : 'white',
                    color: selected ? 'white' : '#334155',
                    border: `2px solid ${color}`,
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: '600',
                    transition: 'all 0.3s',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    alignItems: 'flex-start',
                  }}
                >
                  <span>{label}</span>
                  {active && (
                    <span style={{ fontSize: '11px', fontWeight: '500', color: selected ? '#dbeafe' : '#15803d', backgroundColor: selected ? 'rgba(255,255,255,0.2)' : '#dcfce7', padding: '2px 8px', borderRadius: '8px' }}>
                      🔒 Generated · expires {new Date(rec.expires_at).toLocaleDateString()}
                    </span>
                  )}
                  {archived && (
                    <span style={{ fontSize: '11px', fontWeight: '500', color: selected ? '#fde68a' : '#92400e', backgroundColor: selected ? 'rgba(255,255,255,0.2)' : '#fef3c7', padding: '2px 8px', borderRadius: '8px' }}>
                      📂 Archived · last {new Date(rec.generated_at).toLocaleDateString()}
                    </span>
                  )}
                  {!rec && (
                    <span style={{ fontSize: '11px', fontWeight: '500', color: selected ? '#dbeafe' : '#64748b', backgroundColor: selected ? 'rgba(255,255,255,0.2)' : '#f1f5f9', padding: '2px 8px', borderRadius: '8px' }}>
                      Available
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Generate Button */}
        <button
          onClick={generateDocument}
          disabled={loading || !companyName || !industry || !aiSystem || currentIsActive}
          title={currentIsActive ? 'Already generated for this audit cycle' : 'Generate document'}
          style={{
            width: '100%',
            padding: '18px',
            backgroundColor: loading || !companyName || !industry || !aiSystem || currentIsActive ? '#cbd5e1' : '#10b981',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            fontSize: '18px',
            fontWeight: 'bold',
            cursor: loading || !companyName || !industry || !aiSystem || currentIsActive ? 'not-allowed' : 'pointer',
            transition: 'all 0.3s',
          }}
        >
          {loading
            ? '⏳ Generating Document...'
            : currentIsActive
              ? '✓ Already generated for this period'
              : currentIsArchived
                ? `🔄 Regenerate (last ${new Date(currentRecord.generated_at).toLocaleDateString()})`
                : '✨ Generate Document'}
        </button>

        {currentIsActive && (
          <p style={{ marginTop: '10px', fontSize: '12px', color: '#64748b', textAlign: 'center' }}>
            Next generation available: <strong>{new Date(currentRecord.expires_at).toLocaleDateString()}</strong>
          </p>
        )}

        {isDev && currentRecord && (
          <button
            onClick={resetAuditCycle}
            disabled={resetting}
            title="Dev only: clears expires_at for this doc type so you can regenerate immediately"
            style={{
              width: '100%',
              marginTop: '12px',
              padding: '10px',
              backgroundColor: 'transparent',
              color: resetting ? '#cbd5e1' : '#dc2626',
              border: `1px dashed ${resetting ? '#cbd5e1' : '#dc2626'}`,
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: resetting ? 'wait' : 'pointer',
            }}
          >
            {resetting ? '⏳ Resetting...' : '🔧 Reset audit cycle (dev only)'}
          </button>
        )}
      </div>

      {/* Generated Document Display */}
      {generatedDoc && (
        <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '15px', boxShadow: '0 4px 6px rgba(0,0,0,0.07)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ fontSize: '24px', color: '#0f172a', margin: 0 }}>
              Generated Document
              {currentRecord && (
                <span style={{ marginLeft: '12px', fontSize: '13px', fontWeight: '500', color: '#64748b' }}>
                  · saved {new Date(currentRecord.generated_at).toLocaleDateString()}
                </span>
              )}
            </h2>


            <button
              onClick={downloadPDF}
              disabled={pdfGenerating}
              style={{
                padding: '12px 24px',
                backgroundColor: pdfGenerating ? '#cbd5e1' : '#3b82f6',
                color: 'white', border: 'none', borderRadius: '8px',
                fontSize: '16px', fontWeight: '600',
                cursor: pdfGenerating ? 'wait' : 'pointer',
                display: 'flex', alignItems: 'center', gap: '8px'
              }}
            >
              {pdfGenerating ? <><span>⏳</span><span>Generating PDF...</span></> : <><span>⬇️</span><span>Download as PDF</span></>}
            </button>

          </div>


          <div
            style={{ /* keep existing styles, but remove whiteSpace: 'pre-wrap' and fontFamily: 'monospace' */
              backgroundColor: '#f8fafc',
              padding: '20px',
              borderRadius: '10px',
              border: '2px solid #e2e8f0',
              fontSize: '15px',
              lineHeight: '1.8',
              color: '#1e293b',
              /* if scrollable: */
              maxHeight: '500px',
              overflowY: 'auto'
            }}
            dangerouslySetInnerHTML={{ __html: renderAiMarkdown(generatedDoc) }}
          />



          <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#fef3c7', border: '2px solid #fbbf24', borderRadius: '10px' }}>
            <p style={{ margin: 0, color: '#92400e', fontSize: '14px' }}>
              <strong>⚠️ Disclaimer:</strong> AI‑generated compliance guidance – not legal advice. Always verify with a qualified professional. For questions, contact QuantumAIshield support. Blockchain records are permanent – please confirm before sealing.
            </p>
          </div>
        </div>
      )}

      {/* Help Text */}
      {!generatedDoc && !loading && (
        <div style={{ backgroundColor: '#eff6ff', padding: '25px', borderRadius: '15px', border: '2px solid #3b82f6' }}>
          <h3 style={{ fontSize: '20px', color: '#1e40af', marginBottom: '10px' }}>💡 How this works:</h3>
          <ul style={{ margin: 0, paddingLeft: '20px', color: '#1e3a8a', lineHeight: '1.8' }}>
            <li>Fill in your company details above</li>
            <li>Choose the type of document you need</li>
            <li>Click "Generate Document" and wait 10-20 seconds</li>
            <li>AI will create a professional compliance document</li>
            <li>Download and customize it for your needs</li>
            <li>🔒 Each document type can be regenerated only after the audit cycle expires (saves AI tokens)</li>
          </ul>
        </div>
      )}
    </div>
  );
}

export default DocumentGenerator;